import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import ReactSlidingPane from 'react-sliding-pane'
import NotificationCard from './NotificationCard'
import { NotificationsProps, ProcessItem } from './Processes.d'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import { setNotificationsCounts } from '../../features/security/security.slice'
import Loader from '../common/Loader'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faBell,
    faCheckDouble,
    faCircleCheck,
    faInbox,
    faXmark,
} from '@fortawesome/free-solid-svg-icons'

type TabKey = 'all' | 'unread'

const Notifications: React.FC<NotificationsProps> = ({
    type = 'link',
    trigger,
}) => {
    const dispatch = useAppDispatch()
    const axiosPrivate = useAxiosPrivate()
    const initData = useAppSelector((state: any) => state.security.initData)

    const [isLoading, setIsLoading] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [items, setItems] = useState<ProcessItem[]>([])
    const [activeTab, setActiveTab] = useState<TabKey>('unread')
    const [markAllLoading, setMarkAllLoading] = useState(false)

    let source: ReturnType<typeof axios.CancelToken.source> | undefined

    const loadData = useCallback(() => {
        setIsLoading(true)
        if (source) source.cancel()
        const CancelToken = axios.CancelToken
        source = CancelToken.source()
        axiosPrivate
            .get('/processes/notifications?length=50&start=0', {
                cancelToken: source.token,
            })
            .then((res) => {
                setItems(res.data.data?.items ?? [])
                dispatch(
                    setNotificationsCounts({
                        totalCount: res.data.data?.total_count ?? 0,
                        unreadCount: res.data.data?.unread_count ?? 0,
                    })
                )
            })
            .catch((err) => {
                if (err?.message?.toUpperCase() !== 'CANCELED') {
                    console.error('Failed to load notifications:', err?.message)
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [axiosPrivate, dispatch])

    useEffect(() => {
        return () => {
            source?.cancel()
        }
    }, [initData?.processes?.notifications?.unreadCount])

    const handleOpen = () => setIsOpen(true)

    const handleMarkAllRead = () => {
        const unreadIds = items.filter((i) => !i.is_read).map((i) => i.id)
        if (unreadIds.length === 0) return
        setMarkAllLoading(true)
        Promise.all(
            unreadIds.map((id) =>
                axiosPrivate.post(`/processes/notifications/${id}`, {}, { params: { read: true } })
            )
        )
            .then((responses) => {
                const last = responses[responses.length - 1]
                const data = last?.data?.data
                if (data != null) {
                    dispatch(
                        setNotificationsCounts({
                            totalCount: data.total_count ?? 0,
                            unreadCount: data.unread_count ?? 0,
                        })
                    )
                }
                setItems((prev) => prev.map((i) => ({ ...i, is_read: true })))
            })
            .catch(() => {})
            .finally(() => setMarkAllLoading(false))
    }

    const handleNotificationUpdate = useCallback((id: number, patch: Partial<ProcessItem>) => {
        setItems((prev) =>
            prev.map((i) => (i.id === id ? { ...i, ...patch } : i))
        )
    }, [])

    const handleNotificationRemove = useCallback((id: number) => {
        setItems((prev) => prev.filter((i) => i.id !== id))
    }, [])

    const unreadCount = initData?.processes?.notifications?.unreadCount ?? 0
    const totalCount = initData?.processes?.notifications?.totalCount ?? 0

    const filteredItems =
        activeTab === 'unread' ? items.filter((i) => !i.is_read) : items
    const hasUnread = unreadCount > 0

    return (
        <>
            {trigger ? (
                <span onClick={handleOpen}>{trigger}</span>
            ) : type === 'icon' ? (
                <span onClick={handleOpen} className="nav-link" role="button">
                    Notifications
                </span>
            ) : (
                <span
                    onClick={handleOpen}
                    className="dropdown-item dropdown-menu-footer text-center small"
                    role="button"
                >
                    Read More Notifications
                </span>
            )}
            <ReactSlidingPane
                isOpen={isOpen}
                title=""
                hideHeader
                onRequestClose={() => setIsOpen(false)}
                width="min(540px, 100vw)"
                className="notifications-pane"
                onAfterOpen={loadData}
            >
                <div className="notifications-pane__inner">
                    <header className="notifications-pane__header">
                        <div className="notifications-pane__title-wrap">
                            <span className="notifications-pane__icon-wrap">
                                <FontAwesomeIcon icon={faBell} className="notifications-pane__icon" />
                                {hasUnread && (
                                    <span className="notifications-pane__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                                )}
                            </span>
                            <div>
                                <h2 className="notifications-pane__title">Notifications</h2>
                                <p className="notifications-pane__subtitle">
                                    {unreadCount} unread · {totalCount} total
                                </p>
                            </div>
                        </div>
                        <div className="notifications-pane__header-actions">
                            {hasUnread && (
                                <button
                                    type="button"
                                    className="notifications-pane__mark-all"
                                    onClick={handleMarkAllRead}
                                    disabled={markAllLoading}
                                    title="Mark all as read"
                                >
                                    <FontAwesomeIcon icon={faCheckDouble} spin={markAllLoading} />
                                    <span className="notifications-pane__mark-all-text">Mark all read</span>
                                </button>
                            )}
                            <button
                                type="button"
                                className="notifications-pane__close"
                                onClick={() => setIsOpen(false)}
                                title="Close"
                                aria-label="Close notifications"
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                    </header>

                    <div className="notifications-pane__tabs">
                        <button
                            type="button"
                            className={`notifications-pane__tab ${activeTab === 'unread' ? 'notifications-pane__tab--active' : ''}`}
                            onClick={() => setActiveTab('unread')}
                        >
                            <FontAwesomeIcon icon={faBell} />
                            Unread
                            {unreadCount > 0 && (
                                <span className="notifications-pane__tab-badge">{unreadCount}</span>
                            )}
                        </button>
                        <button
                            type="button"
                            className={`notifications-pane__tab ${activeTab === 'all' ? 'notifications-pane__tab--active' : ''}`}
                            onClick={() => setActiveTab('all')}
                        >
                            <FontAwesomeIcon icon={faInbox} />
                            All
                        </button>
                    </div>

                    <div className="notifications-pane__list-wrap">
                        {isLoading ? (
                            <div className="notifications-pane__loader">
                                <Loader text="Loading notifications…" />
                            </div>
                        ) : filteredItems.length > 0 ? (
                            <ul className="notifications-pane__list" role="list">
                                {filteredItems.map((notification) => (
                                    <li key={notification.id}>
                                        <NotificationCard
                                            type="notification"
                                            item={notification}
                                            onNotificationUpdate={handleNotificationUpdate}
                                            onNotificationRemove={handleNotificationRemove}
                                        />
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="notifications-pane__empty">
                                <span className="notifications-pane__empty-icon">
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                </span>
                                <h3 className="notifications-pane__empty-title">
                                    {activeTab === 'unread' ? 'All caught up' : 'No notifications yet'}
                                </h3>
                                <p className="notifications-pane__empty-text">
                                    {activeTab === 'unread'
                                        ? "You've read all your notifications."
                                        : 'When you get reports or system updates, they’ll show up here.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </ReactSlidingPane>
        </>
    )
}

export default Notifications
